import { useState, useEffect, useCallback, useRef } from "react";

/**
 * useBrowserNotifications - Hook til at håndtere browser-baserede notifikationer
 * Viser notifikationer med lyd når brugeren er logget ind, selv når browser/tab er i baggrunden
 */
export function useBrowserNotifications(eventId, token, isLoggedIn) {
  const [permission, setPermission] = useState(() => {
    return typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  const lastNotificationIdRef = useRef(null);

  // Initialiser lyd-element
  useEffect(() => {
    // Brug en system notification lyd
    audioRef.current = new Audio('/notification-sound.mp3');
    audioRef.current.volume = 0.5;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Anmod om tilladelse til notifikationer
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Browser understøtter ikke notifikationer');
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermission('granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    }

    return false;
  }, []);

  // Vis en browser notifikation
  const showNotification = useCallback((title, body, type = 'normal', onClick) => {
    if (!isLoggedIn) {
      console.log('Bruger er ikke logget ind - springer notifikation over');
      return;
    }

    if (permission !== 'granted') {
      console.warn('Notifikationstilladelse ikke givet');
      return;
    }

    // Bestem ikon baseret på type
    const icon = type === 'urgent' ? '🚨' : type === 'important' ? '⚠️' : '📬';

    try {
      const notification = new Notification(title, {
        body: body,
        icon: icon,
        badge: icon,
        tag: `resovia-${Date.now()}`, // Unik tag for at undgå duplikater
        requireInteraction: type === 'urgent', // Urgent notifications forbliver synlige
        silent: false // Tillad lyd
      });

      // Afspil lyd hvis aktiveret
      if (soundEnabled && audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(err => console.log('Kunne ikke afspille lyd:', err));
      }

      // Håndter klik på notifikation
      notification.onclick = () => {
        window.focus();
        if (onClick) onClick();
        notification.close();
      };

      // Auto-luk efter 10 sekunder (undtagen urgent)
      if (type !== 'urgent') {
        setTimeout(() => notification.close(), 10000);
      }
    } catch (error) {
      console.error('Fejl ved visning af notifikation:', error);
    }
  }, [permission, soundEnabled, isLoggedIn]);

  // Check om notifikationer er understøttet
  const isSupported = 'Notification' in window;

  return {
    permission,
    isSupported,
    soundEnabled,
    setSoundEnabled,
    requestPermission,
    showNotification,
    lastNotificationIdRef
  };
}

export default useBrowserNotifications;
